import React from 'react';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: string;
  subtitle?: string;
  trend?: {
    value: number;
    isPositive: boolean;
    label: string;
  };
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'gray';
  className?: string;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  subtitle,
  trend,
  color = 'blue',
  className = ''
}) => {
  const getColorClasses = () => {
    switch (color) {
      case 'green':
        return {
          bg: 'bg-green-50',
          icon: 'text-green-600',
          text: 'text-green-900',
          subtitle: 'text-green-700'
        };
      case 'yellow':
        return {
          bg: 'bg-yellow-50',
          icon: 'text-yellow-600',
          text: 'text-yellow-900',
          subtitle: 'text-yellow-700'
        };
      case 'red':
        return {
          bg: 'bg-red-50',
          icon: 'text-red-600',
          text: 'text-red-900',
          subtitle: 'text-red-700'
        };
      case 'gray':
        return {
          bg: 'bg-gray-50',
          icon: 'text-gray-600',
          text: 'text-gray-900',
          subtitle: 'text-gray-700'
        };
      default: // blue
        return {
          bg: 'bg-blue-50',
          icon: 'text-blue-600',
          text: 'text-blue-900',
          subtitle: 'text-blue-700'
        };
    }
  };

  const colorClasses = getColorClasses();

  const formatValue = (val: string | number): string => {
    if (typeof val === 'number') {
      return new Intl.NumberFormat().format(val);
    }
    return val;
  };

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-lg ${colorClasses.bg}`}>
          <span className={`text-2xl ${colorClasses.icon}`}>
            {icon}
          </span>
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-500 mb-1">
            {title}
          </p>
          <div className="flex items-baseline">
            <p className={`text-2xl font-semibold ${colorClasses.text}`}>
              {formatValue(value)}
            </p>
            {trend && (
              <span
                className={`ml-2 text-sm font-medium ${
                  trend.isPositive ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {trend.isPositive ? '↗' : '↘'} {Math.abs(trend.value)}% {trend.label}
              </span>
            )}
          </div>
          {subtitle && (
            <p className={`text-sm ${colorClasses.subtitle} mt-1`}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsCard;
